import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Leaf, LayoutDashboard, LogOut, Bell } from "lucide-react";

export default function DashboardShell({ title, nav, active, onNav, children, testId }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileNav, setMobileNav] = useState(false);

  return (
    <div className="min-h-screen bg-background flex" data-testid={testId}>
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 shrink-0 bg-lawn-ink text-white flex-col">
        <Link to="/" className="flex items-center gap-2 px-6 h-16 border-b border-white/10">
          <div className="w-8 h-8 rounded-lg bg-lawn flex items-center justify-center"><Leaf className="w-4 h-4 text-white" /></div>
          <span className="font-display text-lg font-extrabold">LawnLink<span className="text-lawn">Pro</span></span>
        </Link>
        <nav className="flex-1 p-4 space-y-1">
          {nav.map((item) => {
            const Icon = item.icon;
            return (
              <button key={item.key} data-testid={`dashnav-${item.key}`} onClick={() => onNav(item.key)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors ${active === item.key ? "bg-lawn text-white" : "text-white/70 hover:bg-white/5"}`}>
                <Icon className="w-4 h-4" /> {item.label}
              </button>
            );
          })}
        </nav>
        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 rounded-full bg-lawn flex items-center justify-center font-bold text-sm">{user?.name?.[0]?.toUpperCase()}</div>
            <div className="min-w-0">
              <p className="text-sm font-semibold truncate">{user?.name}</p>
              <p className="text-[11px] text-white/50 capitalize">{user?.role}</p>
            </div>
          </div>
          <Button variant="ghost" data-testid="dash-logout-btn" onClick={async () => { await logout(); navigate("/"); }} className="w-full justify-start gap-2 text-white/70 hover:text-white hover:bg-white/5">
            <LogOut className="w-4 h-4" /> Logout
          </Button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="glass-header h-16 flex items-center justify-between px-5 sm:px-8 sticky top-0 z-30">
          <h1 className="font-display text-xl sm:text-2xl font-extrabold text-lawn-ink">{title}</h1>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" className="rounded-full"><Bell className="w-4 h-4" /></Button>
            <Button variant="outline" onClick={() => navigate("/")} className="rounded-full gap-2 hidden sm:flex"><LayoutDashboard className="w-4 h-4" /> Site</Button>
          </div>
        </header>

        {/* Mobile nav */}
        <div className="md:hidden flex gap-2 overflow-x-auto px-4 py-3 border-b border-border bg-white">
          {nav.map((item) => (
            <button key={item.key} onClick={() => onNav(item.key)} className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-semibold ${active === item.key ? "bg-lawn text-white" : "bg-muted text-muted-foreground"}`}>{item.label}</button>
          ))}
        </div>

        <div className="flex-1 p-5 sm:p-8 overflow-x-hidden">{children}</div>
      </div>
    </div>
  );
}
